import { useState, useMemo, useCallback, useEffect } from 'react'
import { getISOWeek } from 'date-fns'
import { es } from 'date-fns/locale'
import { format, startOfWeek, endOfWeek, parseISO } from 'date-fns'
import { StorePerformanceItem, PeriodValidationError } from '../types'
import { Constants } from '../../../../../../../utils/constants'
import { getUserRole } from '../../../../../../../service/auth'
import { Roles } from '../../../../../../../utils/enums/roles'
import { getDivisions, getZones, getUsers, getRoles } from '../../../../../../../utils/methods/geServiceWithItemsFormat'
import { usePeriodValidation } from './usePeriodValidation'
import { isValidDate } from '../utils/periodValidation'
import { SESSION_STORAGE_KEY } from '../constants'

interface UseFiltersReturn {
  periodFrom: Date | null
  setPeriodFrom: (date: Date | null) => void
  periodTo: Date | null
  setPeriodTo: (date: Date | null) => void
  periodWeek: string[]
  setPeriodWeek: (weeks: string[]) => void
  periodYear: number | null
  setPeriodYear: (year: number | null) => void
  periodValidationError: PeriodValidationError | null
  hasPeriodErrors: boolean
  filterDivision: StorePerformanceItem[]
  setFilterDivision: (items: StorePerformanceItem[]) => void
  filterZone: StorePerformanceItem[]
  setFilterZone: (items: StorePerformanceItem[]) => void
  filterUser: StorePerformanceItem[]
  setFilterUser: (items: StorePerformanceItem[]) => void
  filterRole: StorePerformanceItem[]
  setFilterRole: (items: StorePerformanceItem[]) => void
  divisionsOptions: StorePerformanceItem[]
  zonesOptions: StorePerformanceItem[]
  usersOptions: StorePerformanceItem[]
  rolesOptions: StorePerformanceItem[]
  filtersLoading: boolean
  isPartner: boolean
  hideZone: boolean
  handleApplyFilters: () => void
  handleCleanFilters: () => void
  filterActiveCount: number
  displayPeriod: string
  appliedPeriodFrom: Date | null
  appliedPeriodTo: Date | null
  appliedPeriodWeek: string[]
  appliedPeriodYear: number
  resetPeriodValidation: () => void
}

const useFilters = (): UseFiltersReturn => {
  const currentWeek = String(getISOWeek(new Date()))
  const currentYear = new Date().getFullYear()

  const savedFilters = (() => {
    if (typeof window === 'undefined') return null
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return null
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  })()

  const savedPeriodFrom = savedFilters?.periodFrom ? new Date(savedFilters.periodFrom) : null
  const savedPeriodTo = savedFilters?.periodTo ? new Date(savedFilters.periodTo) : null
  const savedPeriodWeek = savedFilters?.periodWeek ?? [currentWeek]
  const savedPeriodYear = savedFilters?.periodYear ?? currentYear

  const initialPeriodFrom = isValidDate(savedPeriodFrom) ? savedPeriodFrom : null
  const initialPeriodTo = isValidDate(savedPeriodTo) ? savedPeriodTo : null
  const initialPeriodWeek = savedPeriodWeek
  const initialPeriodYear = savedPeriodYear

  const {
    periodFrom,
    periodTo,
    periodWeek,
    periodYear,
    periodValidationError,
    hasPeriodErrors,
    setPeriodFrom,
    setPeriodTo,
    setPeriodWeek,
    setPeriodYear,
    resetPeriodValidation,
  } = usePeriodValidation({
    initialPeriodFrom,
    initialPeriodTo,
    initialPeriodWeek,
    initialPeriodYear,
  })

  const [appliedPeriodFrom, setAppliedPeriodFrom] = useState<Date | null>(initialPeriodFrom)
  const [appliedPeriodTo, setAppliedPeriodTo] = useState<Date | null>(initialPeriodTo)
  const [appliedPeriodWeek, setAppliedPeriodWeek] = useState<string[]>(initialPeriodWeek)
  const [appliedPeriodYear, setAppliedPeriodYear] = useState<number>(initialPeriodYear)

  const [filterDivision, setFilterDivision] = useState<StorePerformanceItem[]>([])
  const [filterZone, setFilterZone] = useState<StorePerformanceItem[]>([])
  const [filterUser, setFilterUser] = useState<StorePerformanceItem[]>([])
  const [filterRole, setFilterRole] = useState<StorePerformanceItem[]>([])
  const [divisionsOptions, setDivisionsOptions] = useState<StorePerformanceItem[]>([])
  const [zonesOptions, setZonesOptions] = useState<StorePerformanceItem[]>([])
  const [usersOptions, setUsersOptions] = useState<StorePerformanceItem[]>([])
  const [rolesOptions, setRolesOptions] = useState<StorePerformanceItem[]>([])
  const [filtersLoading, setFiltersLoading] = useState(false)

  const userRole = getUserRole()
  const isPartner = userRole === Roles.PARTNER
  const hideZone = userRole !== null && [Roles.AUDIT, Roles.SUPPORT].includes(userRole)

  useEffect(() => {
    const loadFilterOptions = async () => {
      setFiltersLoading(true)
      try {
        const [divisionsRes, zonesRes, usersRes, rolesRes] = await Promise.all([
          getDivisions().catch(() => ({ items: [], all: [] })),
          getZones().catch(() => ({ fatherZones: [], childZones: [], all: [] })),
          getUsers().catch(() => ({ items: [], all: [] })),
          getRoles().catch(() => ({ items: [], all: [] })),
        ])

        const mapToStoreItem = (items: Array<{ label: string; value: number | string }>): StorePerformanceItem[] =>
          items.map((item) => ({
            id: String(item.value),
            label: item.label,
            value: String(item.value),
          }))

        setDivisionsOptions(mapToStoreItem(divisionsRes?.items ?? []))
        const allZones = [...(zonesRes?.fatherZones ?? []), ...(zonesRes?.childZones ?? [])]
        setZonesOptions(mapToStoreItem(allZones))
        setUsersOptions(mapToStoreItem(usersRes?.items ?? []))
        setRolesOptions(mapToStoreItem(rolesRes?.items ?? []))

        const lowLevelRoles = [Roles.AGENT, Roles.AUDIT, Roles.SUPPORT, Roles.STORE]
        const divisions = mapToStoreItem(divisionsRes?.items ?? [])
        if (userRole !== null && lowLevelRoles.includes(userRole) && divisions.length === 1) {
          setFilterDivision(divisions)
        }
      } catch {
        setDivisionsOptions([])
        setZonesOptions([])
        setUsersOptions([])
        setRolesOptions([])
      } finally {
        setFiltersLoading(false)
      }
    }

    loadFilterOptions()
  }, [])

  const handleApplyFilters = useCallback(() => {
    setAppliedPeriodFrom(periodFrom)
    setAppliedPeriodTo(periodTo)
    setAppliedPeriodWeek(periodWeek)
    setAppliedPeriodYear(periodYear ?? currentYear)
  }, [periodFrom, periodTo, periodWeek, periodYear, currentYear])

  const handleCleanFilters = useCallback(() => {
    resetPeriodValidation()
    setAppliedPeriodFrom(null)
    setAppliedPeriodTo(null)
    setAppliedPeriodWeek([currentWeek])
    setAppliedPeriodYear(currentYear)
    setFilterDivision([])
    setFilterZone([])
    setFilterUser([])
    setFilterRole([])
  }, [resetPeriodValidation, currentWeek, currentYear])

  const filterActiveCount = useMemo(() => {
    let count = 0
    if (isValidDate(appliedPeriodFrom)) count++
    if (isValidDate(appliedPeriodTo)) count++
    if (appliedPeriodWeek.length > 0) count++
    if (filterDivision.length > 0) count++
    if (filterZone.length > 0) count++
    if (filterUser.length > 0) count++
    if (filterRole.length > 0) count++
    return count
  }, [appliedPeriodFrom, appliedPeriodTo, appliedPeriodWeek, filterDivision, filterZone, filterUser, filterRole])

  const displayPeriod = useMemo(() => {
    const hasValidFrom = isValidDate(appliedPeriodFrom)
    const hasValidTo = isValidDate(appliedPeriodTo)

    if (hasValidFrom || hasValidTo) {
      const fromStr = hasValidFrom ? format(appliedPeriodFrom, Constants.dateFormat, { locale: es }) : ''
      const toStr = hasValidTo ? format(appliedPeriodTo, Constants.dateFormat, { locale: es }) : ''
      if (fromStr && toStr) return `${fromStr} al ${toStr}`
      return fromStr || toStr
    }
    if (appliedPeriodWeek.length > 0) {
      const weekLabel = appliedPeriodWeek.length === 1
        ? `Semana ${appliedPeriodWeek[0]}`
        : `Semanas ${appliedPeriodWeek.join(', ')}`
      return `${weekLabel}${appliedPeriodYear ? `, ${appliedPeriodYear}` : ''}`
    }
    const from = startOfWeek(Date.now(), { weekStartsOn: 1 })
    const to = endOfWeek(Date.now(), { weekStartsOn: 1 })
    return `${format(from, Constants.dateFormat, { locale: es })} al ${format(to, Constants.dateFormat, { locale: es })}`
  }, [appliedPeriodFrom, appliedPeriodTo, appliedPeriodWeek, appliedPeriodYear])

  return {
    periodFrom,
    setPeriodFrom,
    periodTo,
    setPeriodTo,
    periodWeek,
    setPeriodWeek,
    periodYear,
    setPeriodYear,
    periodValidationError,
    hasPeriodErrors,
    filterDivision,
    setFilterDivision,
    filterZone,
    setFilterZone,
    filterUser,
    setFilterUser,
    filterRole,
    setFilterRole,
    divisionsOptions,
    zonesOptions,
    usersOptions,
    rolesOptions,
    filtersLoading,
    isPartner,
    hideZone,
    handleApplyFilters,
    handleCleanFilters,
    filterActiveCount,
    displayPeriod,
    appliedPeriodFrom,
    appliedPeriodTo,
    appliedPeriodWeek,
    appliedPeriodYear,
    resetPeriodValidation,
  }
}

export { useFilters }
